import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

export default function SignupForm() {
    const [form, setForm] = useState({
        email: "",
        password: "",
        passwordConfirmation: "",
        role: "",
        agreedToTerms: false,
    });

    const [touched, setTouched] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        setTouched((prev) => ({ ...prev, [name]: true }));
    };

    const handleRoleSelect = (value) => {
        setForm((prev) => ({ ...prev, role: value }));
        setTouched((prev) => ({ ...prev, role: true }));
    };

    const isEmailValid = /^\S+@\S+\.\S+$/.test(form.email);
    const doPasswordsMatch =
        form.password.length > 0 && form.password === form.passwordConfirmation;

    const isFormValid =
        form.role && isEmailValid && doPasswordsMatch && form.agreedToTerms;

    const getInputClass = (field) => {
        if (!touched[field]) return "";
        if (field === "email") return isEmailValid ? "border-green-500" : "border-red-500";
        if (field === "passwordConfirmation") return doPasswordsMatch ? "border-green-500" : "border-red-500";
        return "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);

        setTouched({
            email: true,
            password: true,
            passwordConfirmation: true,
            role: true,
            agreedToTerms: true,
        });

        if (!isFormValid) {
            document.querySelector("form").scrollIntoView({ behavior: "smooth" });
            return;
        }

        setSubmitting(true);

        try {
            const endpoint =
                form.role === "candidate"
                    ? "/api/v1/candidates/signup"
                    : "/api/v1/clients/signup";

            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    [form.role]: {
                        email: form.email,
                        password: form.password,
                        password_confirmation: form.passwordConfirmation,
                        role: form.role,
                    },
                }),
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || "Signup failed");
            }

            setShowConfirmation(true);
        } catch (err) {
            setSubmitError(err.message || "An error occurred");
        } finally {
            setSubmitting(false);
        }
    };

    if (showConfirmation) {
        return (
            <div className="text-center space-y-4 p-6">
                <h2 className="text-2xl font-semibold text-[#5D1F42]">Confirmation Email Sent</h2>
                <p className="text-gray-700">Please check your inbox to confirm your account before logging in.</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <Label>I am...</Label>
                <Select value={form.role} onValueChange={handleRoleSelect}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="candidate">Looking for work</SelectItem>
                        <SelectItem value="client">Looking to hire</SelectItem>
                    </SelectContent>
                </Select>
                {touched.role && !form.role && (
                    <p className="text-sm text-red-500 mt-1">Please select a role</p>
                )}
            </div>

            <div>
                <Label htmlFor="email">Email</Label>
                <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
                    className={getInputClass("email")}
                    required
                />
                {touched.email && !isEmailValid && (
                    <p className="text-sm text-red-500 mt-1">Enter a valid email</p>
                )}
            </div>

            <div>
                <Label htmlFor="password">Password</Label>
                <Input
                    id="password"
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
                    required
                />
                {touched.password && form.password.length < 6 && (
                    <p className="text-sm text-red-500 mt-1">
                        Password must be at least 6 characters
                    </p>
                )}
            </div>

            <div>
                <Label htmlFor="passwordConfirmation">Confirm Password</Label>
                <Input
                    id="passwordConfirmation"
                    name="passwordConfirmation"
                    type="password"
                    value={form.passwordConfirmation}
                    onChange={handleChange}
                    onBlur={() =>
                        setTouched((prev) => ({ ...prev, passwordConfirmation: true }))
                    }
                    className={getInputClass("passwordConfirmation")}
                    required
                />
                {touched.passwordConfirmation && !doPasswordsMatch && (
                    <p className="text-sm text-red-500 mt-1">Passwords do not match</p>
                )}
            </div>

            <div className="flex items-center gap-2">
                <Checkbox
                    id="agreedToTerms"
                    name="agreedToTerms"
                    checked={form.agreedToTerms}
                    onCheckedChange={(checked) =>
                        setForm((prev) => ({ ...prev, agreedToTerms: checked }))
                    }
                />
                <Label htmlFor="agreedToTerms" className="text-sm leading-none">
                    I agree to the{" "}
                    <a href="/terms" className="underline">
                        Terms and Conditions
                    </a>
                </Label>
            </div>
            {touched.agreedToTerms && !form.agreedToTerms && (
                <p className="text-sm text-red-500 mt-1">
                    You must agree to the terms to continue
                </p>
            )}

            {submitError && (
                <div className="text-sm text-red-600 text-center">{submitError}</div>
            )}

            <Button type="submit" className="w-full bg-brand-500" disabled={submitting}>
                {submitting ? "Signing Up..." : "Sign Up"}
            </Button>
        </form>
    );
}
